import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as WinchDownIcon } from '@src/assets/icons/winch-down.svg';
import { ArcButton } from '@src/core/ui';
import { ArcSocketContext } from '@src/core/socket';
import { EnRouteCommandEnum, EnRouteEventTypeEnum } from '@src/features/order/pages/take-off-to-en-route-page/entities';
import { WinchDownActionProps } from './types';
import { useConfirm } from '@src/core/controllers';
import { useFlightProvider } from '@src/features/order/provider';
import { useCommandPending } from '@src/features/order/hooks';
import { arcSpatium } from '@src/core/extensions';
import { useUnitType } from '@src/core/features/settings';
import { CommandStatusEnum } from '@src/features/order/entities';

const ACCEPT_TIMEOUT_MS = 15000;
const COMPLETE_TIMEOUT_MS = 60000;

const WinchDownAction: React.FC<WinchDownActionProps> = (props) => {
  const { serialNumber, sx } = props;
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { arcSocket } = React.useContext(ArcSocketContext);
  const { distanceSensor } = useFlightProvider();
  useUnitType();

  const { isPending, start } = useCommandPending({
    command: CommandStatusEnum.WINCH_DELIVERY,
    serialNumber,
    acceptTimeoutMs: ACCEPT_TIMEOUT_MS,
    completeTimeoutMs: COMPLETE_TIMEOUT_MS,
    timeoutMessage: t('order.winchDown.timeout'),
  });

  const winchDown = async () => {
    const ok = await confirm(
      'Ensure the drop zone is clear.', 'Terrain altitude: ' + arcSpatium(distanceSensor?.current_distance_m, { fromSi: true }) + '. Confirm winch down?'
    );
    if (!ok) return;

    const socket = arcSocket();
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      toast.error(t('order.winchDown.noConnection'));
      return;
    }

    socket.send(
      JSON.stringify({
        type: EnRouteEventTypeEnum.WINCH_COMMAND,
        command: EnRouteCommandEnum.WINCH_DELIVERY,
        serialNumber,
      })
    );

    start();
  };

  return (
    <ArcButton.brand
      sx={{ width: 200, '& .MuiTypography-root': { color: 'background.paper' }, ...sx }}
      startIcon={<WinchDownIcon width={22} height={22} />}
      isLoading={isPending}
      onClick={winchDown}>
       WINCH DOWN
    </ArcButton.brand>
  );
};

export default WinchDownAction;
